import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo, faqSchema } from "../lib/seo.js";
import { track } from "../lib/analytics.js";
import { Section, SectionHeading } from "../components/Section.js";
import {
  COMMISSION_TIERS,
  COMMISSION_PIPELINE,
  COMMISSION_FAQ,
  type CommissionTier,
} from "../content/commission.js";

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Vault Breaker Commissions",
  serviceType: "Custom casino game development",
  provider: {
    "@type": "Organization",
    name: "Vault Breaker Studios",
    url: "https://vaultbreaker.dev",
  },
  description:
    "Productized custom-game builds for licensed casino operators. Reskin from $249 in 5 business days, Variant from $999 in 7 business days, Bespoke from $2,999 in 14 business days. 10% revshare on every deployed game.",
  url: "https://vaultbreaker.dev/commission",
  areaServed: "Worldwide",
};

export function Commission() {
  return (
    <>
      <Seo
        path="/commission"
        title="Custom games shipped in a week · Vault Breaker Studios"
        description="Describe your game. We ship it in 5-14 days. Reskin from $249. Variant from $999. Bespoke from $2,999. We still take only 10% of GGR."
        jsonLd={[SERVICE_JSONLD, faqSchema(COMMISSION_FAQ)]}
      />

      {/* HERO */}
      <Section style={{ paddingTop: 80, paddingBottom: 48 }}>
        <div style={{ maxWidth: 860 }}>
          <Badge kind="fair" style={{ marginBottom: 18 }}>
            COMMISSIONS — NEW
          </Badge>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(48px, 8vw, 104px)",
              lineHeight: 0.95,
              letterSpacing: "-0.015em",
              margin: 0,
              color: "var(--fg-1)",
              textTransform: "uppercase",
            }}
          >
            Custom games.
            <br />
            <span style={{ color: "var(--gold-bright)" }}>Shipped in a week.</span>
          </h1>
          <p
            style={{
              fontSize: 19,
              color: "var(--fg-2)",
              marginTop: 20,
              maxWidth: 680,
              lineHeight: 1.55,
            }}
          >
            Describe your game. We ship it in 5 to 14 business days, on the same engine that runs
            our flagship. Reskin from $249. Variant from $999. Bespoke from $2,999.
          </p>
          <p
            style={{
              fontSize: 15,
              color: "var(--fg-3)",
              marginTop: 14,
              maxWidth: 680,
              lineHeight: 1.55,
            }}
          >
            $249 doesn't cover our cost. The build fee is a commitment deposit; the 10% revshare on
            the deployed game is how we actually earn.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
            <Link
              to="/contact?service=commission"
              onClick={() => track("commission_brief_click", { surface: "commission_hero" })}
              style={{ textDecoration: "none" }}
            >
              <Button variant="gold" style={{ padding: "16px 24px", fontSize: 15 }}>
                START A BRIEF
              </Button>
            </Link>
            <a
              href="#pricing"
              onClick={() => track("commission_pricing_click", { surface: "commission_hero" })}
              style={{ textDecoration: "none" }}
            >
              <Button style={{ padding: "16px 24px", fontSize: 15 }}>SEE PRICING</Button>
            </a>
          </div>
          <div
            style={{
              display: "flex",
              gap: 18,
              marginTop: 32,
              flexWrap: "wrap",
              fontSize: 12,
              color: "var(--fg-3)",
              fontFamily: "var(--font-mono)",
            }}
          >
            <span>· 10% revshare on every deployed game</span>
            <span>· Build fee refunded if we miss the SLA</span>
            <span>· Buy-out option from $9,999</span>
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section>
        <SectionHeading
          eyebrow="How it works"
          title="Three steps. No agency theatre."
          lead="No discovery phase. No deck. No 90-day kickoff. Brief us, we build, you launch."
        />
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          }}
        >
          <HowStep
            n="1"
            title="You describe"
            body="Five-minute brief: tier, base template (or new mechanic), brand assets, target jurisdiction. We confirm or push back the same day."
          />
          <HowStep
            n="2"
            title="We build"
            body="Reskin in 5 days, Variant in 7, Bespoke in 14. Sandbox preview a day before delivery, fixes land same-day."
          />
          <HowStep
            n="3"
            title="You launch"
            body="Production build drops into the RGS surface you already integrated. Same endpoints, same code path — just a new game id."
          />
        </div>
      </Section>

      {/* PRICING */}
      <Section id="pricing">
        <SectionHeading
          eyebrow="Pricing"
          title="Three tiers. Real numbers."
          lead="Same 10% revshare on every tier. The build fee is a commitment deposit, not a margin source — see the honest part below."
        />
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          }}
        >
          {COMMISSION_TIERS.map((tier) => (
            <TierCard key={tier.id} tier={tier} />
          ))}
        </div>
        <p
          style={{
            marginTop: 14,
            fontSize: 12,
            color: "var(--fg-3)",
            fontFamily: "var(--font-mono)",
            lineHeight: 1.55,
          }}
        >
          · GGR = total bets minus total wins. Revshare settled weekly in USDT/USDC. Buy-out is a
          one-time payment that drops the 10% in perpetuity.
        </p>
      </Section>

      {/* PIPELINE */}
      <Section>
        <SectionHeading
          eyebrow="Reskin pipeline"
          title="The 5-day pipeline"
          lead="Day-by-day, what happens after you sign the SOW. Variant adds two days for math iteration; Bespoke runs the same shape over 14 days with more loops on days 2–10."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {COMMISSION_PIPELINE.map((step, i) => (
            <div
              key={step.day}
              style={{
                padding: "20px 24px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
                display: "grid",
                gridTemplateColumns: "90px 1fr",
                gap: 18,
                alignItems: "start",
              }}
            >
              <div>
                <span
                  style={{
                    display: "block",
                    fontFamily: "var(--font-display)",
                    fontSize: 26,
                    color: "var(--gold-bright)",
                    lineHeight: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {step.label}
                </span>
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 17,
                    color: "var(--fg-1)",
                    fontFamily: "var(--font-ui)",
                  }}
                >
                  {step.title}
                </h3>
                <p
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: 14,
                    color: "var(--fg-2)",
                    lineHeight: 1.6,
                  }}
                >
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </Card>
      </Section>

      {/* THE HONEST PART */}
      <Section>
        <SectionHeading eyebrow="The honest part" title="On the build fee" />
        <Card
          style={{
            padding: "32px 28px",
            background:
              "radial-gradient(ellipse at top left, rgba(212,162,76,0.10), transparent 60%), var(--bg-2)",
            border: "1px solid rgba(212,162,76,0.3)",
            display: "grid",
            gap: 16,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "clamp(22px, 3.4vw, 30px)",
              lineHeight: 1.15,
              color: "var(--fg-1)",
              textTransform: "uppercase",
              letterSpacing: "0.005em",
              maxWidth: 760,
            }}
          >
            Why $249 doesn't cover our cost (and why we charge it anyway).
          </h3>
          <p style={{ margin: 0, fontSize: 15, color: "var(--fg-2)", lineHeight: 1.65 }}>
            A Reskin runs us roughly five engineering days plus an art pass. At a normal studio
            rate, that's a multiple of what we're charging. We don't price it that way because we
            don't earn that way — our P&L lives in the 10% revshare on the games once they're
            deployed. The build fee is a commitment deposit: it filters operators who actually
            intend to launch from operators who are kicking tyres.
          </p>
          <p style={{ margin: 0, fontSize: 15, color: "var(--fg-2)", lineHeight: 1.65 }}>
            The trade is symmetric. You put $249 down to lock the calendar; we put a week of
            engineering time down to ship a game we'll co-own the upside of. If the deployed game
            doesn't earn for you, it doesn't earn for us either — and if we miss the SLA, the
            deposit is refunded. Aligned incentives, in writing, on every tier.
          </p>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-3)",
              lineHeight: 1.6,
              borderTop: "1px solid var(--line)",
              paddingTop: 14,
              marginTop: 6,
            }}
          >
            · same 10% on commissions as on flagship · build fee refunded if SLA missed · buy-out
            available if you'd rather pay once
          </div>
        </Card>
      </Section>

      {/* FAQ */}
      <Section>
        <SectionHeading
          eyebrow="FAQ"
          title="Questions operators actually ask"
          lead="If your question isn't here, reply to the brief confirmation email — Federico answers personally."
        />
        <div style={{ display: "grid", gap: 10 }}>
          {COMMISSION_FAQ.map((item, i) => (
            <Card key={i} style={{ padding: 0, overflow: "hidden" }}>
              <details style={{ padding: "0" }}>
                <summary
                  style={{
                    cursor: "pointer",
                    listStyle: "none",
                    padding: "18px 22px",
                    fontFamily: "var(--font-ui)",
                    fontSize: 15,
                    color: "var(--fg-1)",
                    fontWeight: 500,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span>{item.q}</span>
                  <span
                    aria-hidden
                    style={{
                      color: "var(--gold-bright)",
                      fontFamily: "var(--font-mono)",
                      fontSize: 18,
                      lineHeight: 1,
                    }}
                  >
                    +
                  </span>
                </summary>
                <p
                  style={{
                    margin: 0,
                    padding: "0 22px 20px 22px",
                    fontSize: 14,
                    color: "var(--fg-2)",
                    lineHeight: 1.65,
                  }}
                >
                  {item.a}
                </p>
              </details>
            </Card>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section>
        <Card
          style={{
            padding: "48px 32px",
            textAlign: "center",
            background:
              "radial-gradient(ellipse at center, rgba(212,162,76,0.14), transparent 70%), var(--bg-2)",
            border: "1px solid rgba(212,162,76,0.3)",
          }}
        >
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(28px, 5vw, 44px)",
              lineHeight: 1.05,
              letterSpacing: "0.01em",
              margin: 0,
              color: "var(--fg-1)",
              textTransform: "uppercase",
              maxWidth: 720,
              marginInline: "auto",
            }}
          >
            Brief us in five minutes.
            <br />
            Play it next week.
          </h2>
          <p style={{ fontSize: 15, color: "var(--fg-2)", marginTop: 18, marginBottom: 24 }}>
            One short form. Federico replies the same day. If we can't ship what you want, we'll
            tell you on the call — not after the deposit.
          </p>
          <div
            style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}
          >
            <Link
              to="/contact?service=commission"
              onClick={() => track("commission_brief_click", { surface: "commission_final_cta" })}
              style={{ textDecoration: "none" }}
            >
              <Button variant="gold" style={{ padding: "16px 24px", fontSize: 15 }}>
                START A BRIEF
              </Button>
            </Link>
            <Link
              to="/casino"
              onClick={() => track("try_demo_click", { surface: "commission_final_cta" })}
              style={{ textDecoration: "none" }}
            >
              <Button style={{ padding: "16px 24px", fontSize: 15 }}>Play the catalogue</Button>
            </Link>
          </div>
        </Card>
      </Section>
    </>
  );
}

function HowStep({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <Card style={{ display: "grid", gap: 10, padding: 22 }}>
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 32,
          color: "var(--gold-bright)",
          lineHeight: 1,
        }}
      >
        {n}
      </span>
      <h3 style={{ margin: 0, fontSize: 18, color: "var(--fg-1)" }}>{title}</h3>
      <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.6 }}>{body}</p>
    </Card>
  );
}

function TierCard({ tier }: { tier: CommissionTier }) {
  const accent = tier.recommended;
  return (
    <Card
      style={{
        padding: 24,
        display: "grid",
        gap: 16,
        position: "relative",
        background: accent ? "rgba(212,162,76,0.06)" : "var(--bg-2)",
        border: accent ? "1px solid rgba(212,162,76,0.45)" : "1px solid var(--line)",
      }}
    >
      {accent ? (
        <Badge
          kind="fair"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "rgba(212,162,76,0.18)",
            color: "var(--gold-bright)",
            borderColor: "rgba(212,162,76,0.55)",
          }}
        >
          MOST POPULAR
        </Badge>
      ) : null}

      <div>
        <span
          className="label"
          style={{ display: "block", color: "var(--fg-3)", marginBottom: 8 }}
        >
          {tier.name.toUpperCase()}
        </span>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 40,
            color: "var(--fg-1)",
            lineHeight: 1,
            letterSpacing: "-0.01em",
          }}
        >
          {tier.price}
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--fg-3)",
          }}
        >
          ships in {tier.slaLabel}
        </div>
      </div>

      <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
        {tier.summary}
      </p>

      <div>
        <span
          className="label"
          style={{ display: "block", color: "var(--fg-1)", marginBottom: 8, fontSize: 11 }}
        >
          INCLUDED
        </span>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "grid",
            gap: 8,
          }}
        >
          {tier.includes.map((line) => (
            <li
              key={line}
              style={{
                display: "grid",
                gridTemplateColumns: "16px 1fr",
                gap: 10,
                fontSize: 13,
                color: "var(--fg-2)",
                lineHeight: 1.5,
              }}
            >
              <span aria-hidden style={{ color: "var(--green)", fontWeight: 600 }}>
                ✓
              </span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <span
          className="label"
          style={{ display: "block", color: "var(--fg-3)", marginBottom: 8, fontSize: 11 }}
        >
          NOT INCLUDED
        </span>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "grid",
            gap: 6,
          }}
        >
          {tier.notIncludes.map((line) => (
            <li
              key={line}
              style={{
                display: "grid",
                gridTemplateColumns: "16px 1fr",
                gap: 10,
                fontSize: 12,
                color: "var(--fg-3)",
                lineHeight: 1.5,
              }}
            >
              <span aria-hidden style={{ color: "var(--fg-3)" }}>
                ×
              </span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--fg-3)",
          borderTop: "1px solid var(--line)",
          paddingTop: 12,
          lineHeight: 1.5,
        }}
      >
        Buy-out (zero revshare) {tier.buyoutFrom}
      </div>

      <Link
        to={`/contact?service=commission&tier=${tier.id}`}
        onClick={() =>
          track("commission_brief_click", {
            surface: "commission_pricing_card",
            tier: tier.id,
          })
        }
        style={{ textDecoration: "none" }}
      >
        <Button variant={accent ? "gold" : "ghost"} fullWidth style={{ padding: "14px 20px" }}>
          Brief us — {tier.name}
        </Button>
      </Link>
    </Card>
  );
}
